import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { PurchaseCard } from '../components/products/PurchaseCard';
import { useGetPurchasesQuery } from '../store/api/productsApi';

export function PurchasesPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<{
    categoryId?: string;
    page: number;
    limit: number;
  }>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, error } = useGetPurchasesQuery(filters);

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('products.purchases.error.title')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/catalog"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('products.purchases.error.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const purchases = data?.data.purchases || [];
  const pagination = data?.data.pagination;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('products.purchases.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('products.purchases.subtitle')}
            </p>
          </div>

          {/* Purchases List */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
            {purchases.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-neutral-medium text-lg font-body mb-4">
                  {t('products.purchases.empty')}
                </p>
                <Link
                  to="/catalog"
                  className="inline-block px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('products.purchases.emptyAction')}
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {purchases.map((purchase) => (
                  <PurchaseCard key={purchase.transactionId} purchase={purchase} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-6 animate-scale-in">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('products.purchases.pagination.previous')}
              </button>
              
              <div className="px-4 py-2 bg-neutral-dark/50 text-neutral-light font-body rounded-lg">
                {t('products.purchases.pagination.page')} {pagination.page} {t('products.purchases.pagination.of')} {pagination.pages}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('products.purchases.pagination.next')}
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {pagination && (
            <div className="text-center text-neutral-medium font-body mb-6">
              {t('products.purchases.pagination.showing')} {purchases.length} {t('products.purchases.pagination.of')} {pagination.total} {t('products.purchases.pagination.purchases')}
            </div>
          )}

          {/* Back Button */}
          <div className="text-center">
            <Link
              to="/catalog"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
            >
              {t('products.purchases.backToCatalog')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

